import { create } from "zustand";
import { api, onSessionExpired, refreshAccessToken, tokenStore, unwrap } from "@/api/client";
import type { User } from "@/api/types";

type Status = "idle" | "loading" | "authenticated" | "anonymous";

interface AuthState {
  status: Status;
  user: User | null;
  /** Restore a session from the refresh cookie on app start. Safe to call repeatedly. */
  bootstrap: () => Promise<void>;
  /** Store tokens + user returned by login/register/OTP/Google. */
  signIn: (access: string, user: User) => void;
  setUser: (user: User) => void;
  refreshUser: () => Promise<User | null>;
  logout: () => Promise<void>;
  clear: () => void;
}

let bootstrapPromise: Promise<void> | null = null;

export const useAuth = create<AuthState>((set, get) => ({
  status: "idle",
  user: null,

  bootstrap: () => {
    if (bootstrapPromise) return bootstrapPromise;
    set({ status: "loading" });
    bootstrapPromise = (async () => {
      const token = await refreshAccessToken();
      if (!token) {
        set({ status: "anonymous", user: null });
        return;
      }
      try {
        const user = await unwrap(api.GET("/api/v1/me/"));
        set({ status: "authenticated", user });
      } catch {
        tokenStore.set(null);
        set({ status: "anonymous", user: null });
      }
    })().finally(() => {
      bootstrapPromise = null;
    });
    return bootstrapPromise;
  },

  signIn: (access, user) => {
    tokenStore.set(access);
    set({ status: "authenticated", user });
  },

  setUser: (user) => set({ user }),

  refreshUser: async () => {
    if (get().status !== "authenticated") return null;
    const user = await unwrap(api.GET("/api/v1/me/"));
    set({ user });
    return user;
  },

  logout: async () => {
    try {
      await api.POST("/api/v1/auth/logout/", { body: {} });
    } finally {
      get().clear();
    }
  },

  clear: () => {
    tokenStore.set(null);
    set({ status: "anonymous", user: null });
  },
}));

onSessionExpired(() => useAuth.getState().clear());
